import { useEffect } from 'react';
import { ACTION_TYPES, useNewsSearchDispatch, useNewsSearchState } from './news-search-context';
import NewsAppService from '../../../../application/platform/apm/news/news-app-service';
import { INewsListResponse } from '../../../../model/apm/news/response/news-list-response';
import { INewsAddEditRequest } from '../../../../model/apm/news/request/news-add-edit-request';
import { INewsPagedRequest } from '../../../../model/apm/news/request/news-paged-request';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { parseBooleanFilter, parseIdFilter } from '../../../../utils/filter-options';

export const useNewsSearchController = () => {
    const dispatch = useNewsSearchDispatch();
    const state = useNewsSearchState();
    const { isSaving, runSaving, runWithLoading, run, showSuccess, confirmDelete } = useActionRunner();
    const { updateParameters, triggerSearch, ...searchHandlers } = useSearchActions<INewsPagedRequest>(
        dispatch, ACTION_TYPES, { term: undefined, categoryId: undefined, isPublished: undefined },
    );

    useEffect(() => {
        run('Não foi possível carregar as categorias de notícia', async () => {
            dispatch({ type: ACTION_TYPES.setCategories, payload: await NewsAppService.listCategories() });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-term') updateParameters({ term: value || undefined });
        if (name === 'select-category') updateParameters({ categoryId: parseIdFilter(value) });
        if (name === 'select-status') updateParameters({ isPublished: parseBooleanFilter(value) });
    }

    // A listagem não traz resumo e texto, então a edição busca a notícia completa
    function openDrawer(row?: INewsListResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        runWithLoading('Não foi possível carregar a notícia', async () => {
            dispatch({ type: ACTION_TYPES.openDrawer, payload: await NewsAppService.getById(row.id) });
        });
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    function handleSave(request: INewsAddEditRequest) {
        const editing = state.drawer.news;
        return runSaving('Não foi possível salvar a notícia', async () => {
            await (editing ? NewsAppService.edit(editing.id, request) : NewsAppService.add(request));
            showSuccess(editing ? 'Notícia atualizada com sucesso!' : 'Notícia cadastrada como rascunho. Publique-a para exibir no portal.');
            closeDrawer();
            triggerSearch();
        });
    }

    function handleTogglePublication(row: INewsListResponse) {
        const isPublished = !row.isPublished;
        runWithLoading('Não foi possível alterar a publicação da notícia', async () => {
            await NewsAppService.changePublication(row.id, isPublished);
            showSuccess(isPublished ? 'Notícia publicada com sucesso!' : 'Notícia despublicada. Ela não aparece mais no portal.');
            triggerSearch();
        });
    }

    function handleDelete(row: INewsListResponse) {
        confirmDelete(`Tem certeza que deseja excluir a notícia "${row.title}"?`, () => run('Não foi possível excluir a notícia', async () => {
            await NewsAppService.remove(row.id);
            showSuccess('Notícia excluída com sucesso!');
            triggerSearch();
        }));
    }

    return {
        isSaving,
        handleOnChange, ...searchHandlers,
        openDrawer, closeDrawer, handleSave, handleTogglePublication, handleDelete,
    };
};
